/**
 * Ventanas de escritura de **Gestión → Fletes**.
 *
 * Portan las pantallas del panel anterior:
 * - `menu/compra/flete_create.php` + `Compra::get_factura_flete` → **nuevo flete**,
 *   eligiendo las compras que agrupa y el costo de flete de cada línea.
 * - `menu/compra/proveedores_transporte.php` + `Compra::{agregar,editar}_proveedor`
 *   → **transportistas**.
 * - el modal de `flete.php` que corregía los precios (`actualizar_detalles_flete`).
 *
 * Reglas conservadas:
 * - Sólo se pueden fletar compras **sin flete** (`estado_compra_flete = 0`).
 * - Al asignar un flete, cada línea de la compra guarda su `precio_flete` y su
 *   **C.P.A.** en `precio_total = basico + precio_flete`.
 * - El RUC del transportista no se repite y un transportista con fletes activos
 *   no se elimina: se desactiva.
 */

import { useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';

import { Alert, ICONO_EN_ALERTA } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ConfirmDialog, Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Select } from '@/components/ui/select';
import { ApiError } from '@/lib/http/api-error';

import { fechaCorta, hoyIso, monto } from '../gestion-formato';
import {
  useCatalogosFlete,
  useEliminarTransportista,
  useGuardarTransportista,
  useRegistrarFlete,
  useTransportistas,
} from '../compras.api';
import type { CompraFleteable } from '../compras.types';

const CONTROL =
  'h-11 w-full rounded-control border border-ink-200 bg-white px-3 text-sm text-ink-900 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

const ACCION_CAMPO = 'flex items-end gap-2';

interface AvisoProps {
  onAviso: (mensaje: string) => void;
  onClose: () => void;
}

/** Flete de una línea: el importe que se le reparte. */
type RepartoFlete = Record<number, number>;

/* ------------------------------------------------------------------ *
 * Nuevo flete
 * ------------------------------------------------------------------ */

export function FleteNuevoDialog({ abierto, onAviso, onClose }: AvisoProps & { abierto: boolean }) {
  const catalogos = useCatalogosFlete(abierto);
  const registrar = useRegistrarFlete();

  const [cabecera, setCabecera] = useState({
    tipo_comprobante_id: 2,
    serie: '',
    numero: '',
    proveedor_id: 0,
    fecha_emision: hoyIso(),
    fecha_vencimiento: '',
    moneda: 'SOLES' as 'SOLES' | 'DOLARES',
    tipo_cambio: 1,
    exoneracion_igv: 'NO' as 'SI' | 'NO',
    detraccion: 'NO' as 'SI' | 'NO',
    monto_detraccion: 0,
    importe_total: 0,
  });
  const [compras, setCompras] = useState<number[]>([]);
  const [reparto, setReparto] = useState<RepartoFlete>({});
  const [error, setError] = useState<string | null>(null);

  const disponibles = catalogos.data?.compras ?? [];
  const elegidas = disponibles.filter((compra) => compras.includes(compra.id));

  const totalRepartido = elegidas.reduce(
    (suma, compra) =>
      suma + compra.productos.reduce((subtotal, linea) => subtotal + (reparto[linea.id] ?? 0) * linea.cantidad, 0),
    0,
  );

  const alternarCompra = (compra: CompraFleteable) =>
    setCompras((previas) =>
      previas.includes(compra.id) ? previas.filter((id) => id !== compra.id) : [...previas, compra.id],
    );

  const enviar = () => {
    setError(null);
    registrar.mutate(
      {
        ...cabecera,
        numero: cabecera.numero,
        importe_total: Number(cabecera.importe_total) || Number(totalRepartido.toFixed(4)),
        monto_detraccion: Number(cabecera.monto_detraccion) || 0,
        fecha_vencimiento: cabecera.fecha_vencimiento || null,
        compra_ids: compras,
        productos: elegidas.flatMap((compra) =>
          compra.productos.map((linea) => ({
            id_producto: linea.id,
            precio_flete: reparto[linea.id] ?? 0,
          })),
        ),
      },
      {
        onSuccess: (respuesta) => {
          onAviso(respuesta.mensaje);
          onClose();
        },
        onError: (fallo) =>
          setError(fallo instanceof ApiError ? fallo.message : 'No se pudo registrar el flete.'),
      },
    );
  };

  return (
    <Dialog
      open={abierto}
      onClose={onClose}
      size="xl"
      title="Nuevo flete"
      description="Sólo aparecen las compras sin flete. El costo de flete de cada línea se suma a su básico para formar el C.P.A."
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancelar
          </Button>
          <Button size="sm" loading={registrar.isPending} onClick={enviar}>
            Registrar flete
          </Button>
        </>
      }
    >
      {catalogos.isLoading ? <Skeleton className="h-64 w-full" /> : null}

      <div className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <label className="flex flex-col gap-1.5 text-sm">
            <span className="font-medium text-ink-700">Tipo de comprobante</span>
            <Select
              className={CONTROL}
              value={cabecera.tipo_comprobante_id}
              onChange={(evento) =>
                setCabecera((previa) => ({ ...previa, tipo_comprobante_id: Number(evento.target.value) }))
              }
            >
              {(catalogos.data?.tipos_comprobante ?? []).map((tipo) => (
                <option key={tipo.id} value={tipo.id}>
                  {tipo.nombre}
                </option>
              ))}
            </Select>
          </label>

          <label className="flex flex-col gap-1.5 text-sm">
            <span className="font-medium text-ink-700">Transportista</span>
            <Select
              className={CONTROL}
              value={cabecera.proveedor_id}
              onChange={(evento) =>
                setCabecera((previa) => ({ ...previa, proveedor_id: Number(evento.target.value) }))
              }
            >
              <option value={0}>SELECCIONE</option>
              {(catalogos.data?.transportistas ?? []).map((item) => (
                <option key={item.id} value={item.id}>
                  {item.nombre} · {item.ruc}
                </option>
              ))}
            </Select>
          </label>

          <Input
            label="Serie"
            value={cabecera.serie}
            onChange={(evento) => setCabecera((previa) => ({ ...previa, serie: evento.target.value }))}
          />
          <Input
            label="Número"
            value={cabecera.numero}
            onChange={(evento) => setCabecera((previa) => ({ ...previa, numero: evento.target.value }))}
          />
          <Input
            label="Fecha de emisión"
            type="date"
            value={cabecera.fecha_emision}
            onChange={(evento) => setCabecera((previa) => ({ ...previa, fecha_emision: evento.target.value }))}
          />
          <Input
            label="Fecha de vencimiento"
            type="date"
            value={cabecera.fecha_vencimiento}
            onChange={(evento) =>
              setCabecera((previa) => ({ ...previa, fecha_vencimiento: evento.target.value }))
            }
          />
          <Input
            label="Importe total (S/)"
            type="number"
            step="0.01"
            value={cabecera.importe_total}
            onChange={(evento) =>
              setCabecera((previa) => ({ ...previa, importe_total: Number(evento.target.value) }))
            }
          />
          <Input
            label="Tipo de cambio"
            type="number"
            step="0.001"
            value={cabecera.tipo_cambio}
            onChange={(evento) =>
              setCabecera((previa) => ({ ...previa, tipo_cambio: Number(evento.target.value) }))
            }
          />
          <label className="flex flex-col gap-1.5 text-sm">
            <span className="font-medium text-ink-700">Exoneración de IGV</span>
            <Select
              className={CONTROL}
              value={cabecera.exoneracion_igv}
              onChange={(evento) =>
                setCabecera((previa) => ({ ...previa, exoneracion_igv: evento.target.value as 'SI' | 'NO' }))
              }
            >
              <option value="NO">NO</option>
              <option value="SI">SÍ</option>
            </Select>
          </label>
          <label className="flex flex-col gap-1.5 text-sm">
            <span className="font-medium text-ink-700">Detracción</span>
            <Select
              className={CONTROL}
              value={cabecera.detraccion}
              onChange={(evento) =>
                setCabecera((previa) => ({ ...previa, detraccion: evento.target.value as 'SI' | 'NO' }))
              }
            >
              <option value="NO">NO</option>
              <option value="SI">SÍ</option>
            </Select>
          </label>
          {cabecera.detraccion === 'SI' ? (
            <Input
              label="Monto de la detracción (S/)"
              type="number"
              step="0.01"
              value={cabecera.monto_detraccion}
              onChange={(evento) =>
                setCabecera((previa) => ({ ...previa, monto_detraccion: Number(evento.target.value) }))
              }
            />
          ) : null}
          <label className="flex flex-col gap-1.5 text-sm">
            <span className="font-medium text-ink-700">Moneda</span>
            <Select
              className={CONTROL}
              value={cabecera.moneda}
              onChange={(evento) =>
                setCabecera((previa) => ({ ...previa, moneda: evento.target.value as 'SOLES' | 'DOLARES' }))
              }
            >
              <option value="SOLES">SOLES</option>
              <option value="DOLARES">DÓLARES</option>
            </Select>
          </label>
        </div>

        <section className="rounded-card border border-ink-100 bg-white p-4">
          <h3 className="mb-3 text-sm font-semibold text-ink-800">
            Compras sin flete ({disponibles.length})
          </h3>

          {disponibles.length === 0 ? (
            <p className="rounded-card border border-dashed border-ink-200 p-4 text-center text-sm text-ink-500">
              No hay compras pendientes de flete.
            </p>
          ) : null}

          <div className="max-h-[40vh] space-y-3 overflow-auto">
            {disponibles.map((compra) => (
              <div key={compra.id} className="rounded-card border border-ink-100 p-3">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="size-4 accent-brand-500"
                    checked={compras.includes(compra.id)}
                    onChange={() => alternarCompra(compra)}
                  />
                  <span className="font-semibold text-ink-900">{compra.comprobante}</span>
                  <span className="text-xs text-ink-500">
                    {fechaCorta(compra.fecha_emision)} · {monto(compra.importe_total)}
                  </span>
                </label>

                {compras.includes(compra.id) ? (
                  <div className="mt-2 space-y-2">
                    {compra.productos.map((linea) => (
                      <div key={linea.id} className={ACCION_CAMPO}>
                        <span className="min-w-[200px] flex-1 pb-3 text-sm text-ink-700">
                          {linea.producto}
                          <span className="block text-xs text-ink-500">
                            {linea.cantidad} × básico {monto(linea.basico)}
                          </span>
                        </span>
                        <Input
                          label="Flete unitario (S/)"
                          type="number"
                          step="0.0001"
                          containerClassName="w-36"
                          value={reparto[linea.id] ?? 0}
                          onChange={(evento) =>
                            setReparto((previo) => ({ ...previo, [linea.id]: Number(evento.target.value) }))
                          }
                        />
                        <p className="w-28 pb-3 text-right text-sm font-semibold text-ink-800">
                          C.P.A. {monto(linea.basico + (reparto[linea.id] ?? 0))}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            ))}
          </div>

          <p className="mt-3 text-sm text-ink-600">
            Flete del reparto: <strong className="text-ink-900">{monto(totalRepartido)}</strong> · importe
            total del comprobante:{' '}
            <strong className="text-ink-900">
              {monto(Number(cabecera.importe_total) || totalRepartido)}
            </strong>
          </p>
        </section>

        {error ? (
          <Alert variant="error" title="No se pudo registrar">
            {error}
          </Alert>
        ) : null}
      </div>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ *
 * Transportistas
 * ------------------------------------------------------------------ */

export function TransportistasDialog({ abierto, onAviso, onClose }: AvisoProps & { abierto: boolean }) {
  const consulta = useTransportistas(abierto);
  const guardar = useGuardarTransportista();
  const eliminar = useEliminarTransportista();

  const [formulario, setFormulario] = useState({
    id: undefined as number | undefined,
    nombre: '',
    ruc: '',
    telefono: '',
    detalle: '',
  });
  const [porEliminar, setPorEliminar] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const transportistas = consulta.data?.transportistas ?? [];

  const enviar = () => {
    setError(null);
    guardar.mutate(formulario, {
      onSuccess: (respuesta) => {
        onAviso(respuesta.mensaje);
        setFormulario({ id: undefined, nombre: '', ruc: '', telefono: '', detalle: '' });
      },
      onError: (fallo) =>
        setError(fallo instanceof ApiError ? fallo.message : 'No se pudo guardar el transportista.'),
    });
  };

  return (
    <Dialog
      open={abierto}
      onClose={onClose}
      size="lg"
      title="Proveedores de transporte"
      description="El RUC no se repite. Un transportista con fletes activos no se elimina."
      footer={
        <Button variant="ghost" size="sm" onClick={onClose}>
          Cerrar
        </Button>
      }
    >
      {error ? (
        <Alert variant="error" title="No se pudo guardar">
          {error}
        </Alert>
      ) : null}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <Input
          label="Razón social"
          value={formulario.nombre}
          onChange={(evento) => setFormulario((previo) => ({ ...previo, nombre: evento.target.value }))}
        />
        <Input
          label="RUC"
          value={formulario.ruc}
          onChange={(evento) => setFormulario((previo) => ({ ...previo, ruc: evento.target.value }))}
        />
        <Input
          label="Teléfono"
          value={formulario.telefono}
          onChange={(evento) => setFormulario((previo) => ({ ...previo, telefono: evento.target.value }))}
        />
        <Input
          label="Detalle"
          containerClassName="sm:col-span-2"
          value={formulario.detalle}
          onChange={(evento) => setFormulario((previo) => ({ ...previo, detalle: evento.target.value }))}
        />
        <div className="flex items-end">
          <Button
            size="sm"
            loading={guardar.isPending}
            leftIcon={<Plus className="size-4" />}
            onClick={enviar}
          >
            {formulario.id === undefined ? 'Añadir' : 'Guardar'}
          </Button>
        </div>
      </div>

      {consulta.isLoading ? <Skeleton className="h-40 w-full" /> : null}

      <ul className="max-h-[45vh] space-y-2 overflow-auto">
        {transportistas.map((item) => (
          <li
            key={item.id}
            className="flex flex-wrap items-center justify-between gap-2 rounded-card border border-ink-100 p-3"
          >
            <div>
              <p className="font-semibold text-ink-900">{item.nombre ?? 'Sin nombre'}</p>
              <p className="text-xs text-ink-500">
                RUC {item.ruc ?? '—'} · {item.telefono ?? 'sin teléfono'} · {item.fletes} flete
                {item.fletes === 1 ? '' : 's'}
                {item.descripcion ? ` · ${item.descripcion}` : ''}
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Badge variant={item.estado ? 'success' : 'neutral'}>
                {item.estado ? 'ACTIVO' : 'INACTIVO'}
              </Badge>
              <Button
                variant="ghost"
                size="sm"
                onClick={() =>
                  setFormulario({
                    id: item.id,
                    nombre: item.nombre ?? '',
                    ruc: (item.ruc ?? '').trim(),
                    telefono: item.telefono ?? '',
                    detalle: item.descripcion ?? '',
                  })
                }
              >
                Editar
              </Button>
              <Button
                variant="ghost"
                size="sm"
                leftIcon={<Trash2 className="size-4" />}
                onClick={() => setPorEliminar(item.id)}
              >
                Eliminar
              </Button>
            </div>
          </li>
        ))}
      </ul>

      <ConfirmDialog
        open={porEliminar !== null}
        title="Eliminar transportista"
        description="Si tuviera fletes activos no se eliminará; el sistema te lo dirá."
        confirmLabel="Eliminar"
        loading={eliminar.isPending}
        onClose={() => setPorEliminar(null)}
        onConfirm={() => {
          const id = porEliminar;

          setPorEliminar(null);

          if (id === null) return;

          eliminar.mutate(id, {
            onSuccess: (respuesta) => onAviso(respuesta.mensaje),
            onError: (fallo) =>
              setError(fallo instanceof ApiError ? fallo.message : 'No se pudo eliminar el transportista.'),
          });
        }}
      />
    </Dialog>
  );
}

/** Aviso de lo que hace el reparto del flete, para el pie de la pantalla. */
export function AvisoFlete() {
  return (
    <Alert variant="info" title="Cómo se reparte el flete">
      <span className={ICONO_EN_ALERTA}>
        El flete agrupa compras y reparte su costo entre las líneas: cada línea guarda su{' '}
        <strong>precio de flete</strong> y su <strong>C.P.A.</strong> en `precio_total`. Anular un
        flete libera sus compras para poder volver a fletarlas.
      </span>
    </Alert>
  );
}
